/**
 * Mobile Pending Sync Page
 * Shows all pending offline actions with sync status, retry options, and conflict display
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, RefreshCw, Clock, CheckCircle, AlertTriangle, 
  Trash2, FileText, AlertCircle, ClipboardList, Pill, CheckSquare, Paperclip
} from 'lucide-react';
import { 
  getAllQueuedActions, 
  deleteQueuedAction, 
  getQueueStats,
  ACTION_TYPES, 
  ACTION_STATUS 
} from '@/lib/offline/actionQueue';
import { syncAllActions, syncAction, isOnline, getConflicts, dismissConflict, dismissAllConflicts } from '@/lib/offline/syncService';
import { OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import { ConflictAlert, ConflictsList, getConflictMessage } from '@/components/mobile/ConflictDisplay';

const ACTION_CONFIG = {
  [ACTION_TYPES.CHECKIN]: { label: 'Shift Check-in', icon: Clock, color: 'text-blue-500' },
  [ACTION_TYPES.CHECKOUT]: { label: 'Shift Check-out', icon: CheckCircle, color: 'text-green-500' },
  [ACTION_TYPES.PROGRESS_NOTE]: { label: 'Progress Note', icon: FileText, color: 'text-purple-500' },
  [ACTION_TYPES.INCIDENT_REPORT]: { label: 'Incident Report', icon: AlertCircle, color: 'text-red-500' },
  [ACTION_TYPES.MEDICATION_LOG]: { label: 'Medication Log', icon: Pill, color: 'text-amber-500' },
  [ACTION_TYPES.TASK_COMPLETE]: { label: 'Task Complete', icon: CheckSquare, color: 'text-green-500' },
  [ACTION_TYPES.HANDOVER_NOTE]: { label: 'Handover Note', icon: ClipboardList, color: 'text-indigo-500' },
  [ACTION_TYPES.ATTACHMENT_UPLOAD]: { label: 'Attachment', icon: Paperclip, color: 'text-gray-500' },
};

const STATUS_CONFIG = {
  [ACTION_STATUS.PENDING]: { 
    label: 'Pending Sync', 
    badge: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    icon: Clock 
  },
  [ACTION_STATUS.SYNCED]: { 
    label: 'Synced', 
    badge: 'bg-green-500/10 text-green-600 border-green-500/20',
    icon: CheckCircle 
  },
  [ACTION_STATUS.FAILED]: { 
    label: 'Sync Failed', 
    badge: 'bg-red-500/10 text-red-600 border-red-500/20',
    icon: AlertTriangle 
  },
};

export default function MobilePendingSyncPage() {
  const navigate = useNavigate();
  const [online, setOnline] = useState(isOnline());
  const [actions, setActions] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [stats, setStats] = useState({ pending: 0, synced: 0, failed: 0 });
  const [syncing, setSyncing] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [activeTab, setActiveTab] = useState('queue');

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load actions and conflicts
  const loadActions = useCallback(async () => {
    try {
      const allActions = await getAllQueuedActions();
      const queueStats = await getQueueStats();
      const currentConflicts = getConflicts();
      setActions(allActions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setStats(queueStats);
      setConflicts(currentConflicts);
    } catch (err) {
      console.error('Failed to load actions:', err);
    }
  }, []);

  useEffect(() => {
    loadActions();
    
    // Listen for conflict changes
    const handleConflictChange = (e) => {
      setConflicts(e.detail || []);
    };
    window.addEventListener('syncConflicts', handleConflictChange);
    return () => window.removeEventListener('syncConflicts', handleConflictChange);
  }, [loadActions]);

  // Sync all pending actions
  const handleSyncAll = async () => {
    if (!online) {
      toast.error('Cannot sync while offline');
      return;
    }

    setSyncing(true);
    try {
      const results = await syncAllActions();
      await loadActions();
      
      if (results.synced > 0) {
        toast.success(`${results.synced} action(s) synced successfully`);
      }
      if (results.failed > 0) {
        toast.error(`${results.failed} action(s) failed to sync`);
      }
      if (results.synced === 0 && results.failed === 0) {
        toast.info('No actions to sync');
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Retry single action
  const handleRetry = async (action) => {
    if (!online) {
      toast.error('Cannot sync while offline');
      return;
    }

    setSyncingId(action.id);
    try {
      const result = await syncAction(action);
      await loadActions();
      
      if (result.success) {
        toast.success('Action synced successfully');
      } else {
        toast.error(result.error || 'Sync failed');
      }
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  // Delete action
  const handleDelete = async (actionId) => {
    try {
      await deleteQueuedAction(actionId);
      await loadActions();
      toast.success('Action deleted');
    } catch (err) {
      toast.error('Failed to delete action');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const pendingOrFailed = actions.filter(
    a => a.status === ACTION_STATUS.PENDING || a.status === ACTION_STATUS.FAILED
  );

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-pending-sync-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Sync Queue</h1>
            <p className="text-xs text-muted-foreground">
              {stats.pending} pending, {stats.failed} failed
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleSyncAll}
            disabled={syncing || !online || pendingOrFailed.length === 0}
            data-testid="sync-all-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            Sync All
          </Button>
        </div>
      </div>

      {/* Offline Indicator */}
      <div className="px-4 pt-4">
        <OfflineIndicator isOffline={!online} />
      </div>

      {/* Stats Summary */}
      <div className="px-4 pt-4">
        <div className="grid grid-cols-4 gap-2">
          <Card className="border-amber-500/20">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </CardContent>
          </Card>
          <Card className="border-red-500/20">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </CardContent>
          </Card>
          <Card className="border-orange-500/20">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-orange-600">{conflicts.length}</div>
              <div className="text-xs text-muted-foreground">Conflicts</div>
            </CardContent>
          </Card>
          <Card className="border-green-500/20">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.synced}</div>
              <div className="text-xs text-muted-foreground">Synced</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tabs for Queue and Conflicts */}
      <div className="px-4 pt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="queue" className="flex-1">
              Queue {pendingOrFailed.length > 0 && `(${pendingOrFailed.length})`}
            </TabsTrigger>
            <TabsTrigger value="conflicts" className="flex-1">
              Conflicts {conflicts.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-xs">{conflicts.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Queue Tab */}
          <TabsContent value="queue" className="mt-4">
            {/* Actions List */}
            <div className="space-y-3">
              {actions.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <h3 className="font-medium">All Synced</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      No pending offline actions
                    </p>
                  </CardContent>
                </Card>
              ) : (
                actions.map(action => {
                  const config = ACTION_CONFIG[action.actionType] || { 
                    label: action.actionType, 
                    icon: FileText, 
                    color: 'text-gray-500' 
                  };
                  const statusConfig = STATUS_CONFIG[action.status] || STATUS_CONFIG[ACTION_STATUS.PENDING];
                  const Icon = config.icon;
                  const StatusIcon = statusConfig.icon;
                  const isSyncingThis = syncingId === action.id;

                  return (
                    <Card key={action.id} data-testid={`action-${action.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg bg-muted ${config.color}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{config.label}</span>
                              <Badge variant="outline" className={`text-xs ${statusConfig.badge}`}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </div>
                            
                            <p className="text-xs text-muted-foreground">
                              {formatDate(action.createdAt)}
                              {action.retryCount > 0 && (
                                <span className="ml-2 text-red-500">
                                  ({action.retryCount} retries)
                                </span>
                              )}
                            </p>
                            
                            {/* Preview of payload */}
                            <p className="text-xs text-muted-foreground mt-1 truncate">
                              {action.payload?.content?.substring(0, 50) || 
                               action.payload?.description?.substring(0, 50) ||
                               action.payload?.shiftId ||
                               'No preview available'}
                              {(action.payload?.content?.length > 50 || action.payload?.description?.length > 50) && '...'}
                            </p>

                            {/* Error message */}
                            {action.status === ACTION_STATUS.FAILED && action.errorMessage && (
                              <p className="text-xs text-red-500 mt-1">
                                Error: {action.errorMessage}
                              </p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col gap-1">
                            {(action.status === ACTION_STATUS.PENDING || action.status === ACTION_STATUS.FAILED) && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => handleRetry(action)}
                                disabled={!online || isSyncingThis}
                                data-testid={`retry-${action.id}`}
                              >
                                <RefreshCw className={`h-4 w-4 ${isSyncingThis ? 'animate-spin' : ''}`} />
                              </Button>
                            )}
                            {action.status !== ACTION_STATUS.SYNCED && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                onClick={() => handleDelete(action.id)}
                                data-testid={`delete-${action.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Conflicts Tab */}
          <TabsContent value="conflicts" className="mt-4">
            {conflicts.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <h3 className="font-medium">No Conflicts</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    All actions synced without conflicts
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {conflicts.length > 1 && (
                  <div className="flex justify-end">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        dismissAllConflicts();
                        setConflicts([]);
                      }}
                    >
                      Dismiss All ({conflicts.length})
                    </Button>
                  </div>
                )}
                
                {conflicts.map((conflict, index) => (
                  <ConflictAlert
                    key={conflict.clientActionId || index}
                    conflict={conflict}
                    onDismiss={() => {
                      dismissConflict(conflict.clientActionId);
                      setConflicts(prev => prev.filter(c => c.clientActionId !== conflict.clientActionId));
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Info Footer */}
      <div className="px-4 pb-8">
        <p className="text-xs text-center text-muted-foreground">
          Actions sync automatically when you&apos;re online.
          <br />
          Synced actions are kept for 24 hours.
        </p>
      </div>
    </div>
  );
}
